// Layer 3 (Transformation): the analysis envelope projected for the drawer's
// evidence-window and data-quality reads. Pure — no React, no fetch, no
// engine mutation. Every figure is a field read: nothing here recomputes a
// number the analytics service already carried.

import type {
  AnalysisRequest,
  AnalysisResult,
} from '@/shared/contracts/analytics/v1';
import {
  asCaseTime,
  diffMinutes,
  formatDayLabel,
  formatElapsedMinutes,
} from '@/versions/v1/shared/clock.derive';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';
import { assertUsd, formatCurrency, formatCurrencyBare } from '@/versions/v1/shared/money';
import type { PlantRoleId } from '@/versions/v1/schema/plant-role';
import { ROLE_SHORT_LABEL } from '@/versions/v1/shared/bunker-hill-org-seed';

type Result = AnalysisResult;
type Slices = AnalysisRequest['slices'];

/** The envelope's UTC stamp read as plant clock 1:1 (recorded seed decision). */
export function envelopeClock(iso: string): string {
  return iso.slice(0, 16);
}

/** `HH:MM` of an already-clock-shaped stamp. */
function clock(at: string): string {
  return at.split('T')[1] ?? '00:00';
}

/** An envelope stamp labelled on the deck calendar (`Sun 08 · 19:00`); the
 * window line and the replay clock share it so the two never disagree. */
export function caseClockLabel(iso: string): string {
  const at = envelopeClock(iso);
  return `${formatDayLabel(asCaseTime(at))} · ${clock(at)}`;
}

export type WindowRead = { start: string; end: string; label: string };

/** The evidence window the analysis ran over, labelled on the deck calendar. */
export function windowRead(result: Result): WindowRead {
  const start = envelopeClock(result.window.start);
  const end = envelopeClock(result.window.end);
  const label = `${caseClockLabel(start)} → ${caseClockLabel(end)}`;
  return { start, end, label };
}

export type RoleRow = {
  signalRole: string;
  sourceTag: string;
  missing: number;
  bad: number;
  warmUp: number;
  unitMismatch: number;
};

export type DataQualityRead = {
  evaluability: Result['data_quality']['evaluability'];
  reason: Result['data_quality']['reason'];
  confidence: Result['data_quality']['confidence'];
  /** Rounded percent, or null when the service gave no confidence (not evaluable). Never defaulted. */
  confidencePct: number | null;
  missingPoints: number;
  badQualityPoints: number;
  roles: RoleRow[];
};

/**
 * The envelope's own data-quality verdict, plus its per-role counts dressed
 * with the source tag the knowledge slice gives each role. Rows are sorted by
 * volume of trouble (missing + bad, descending) and then by role id, so the
 * tag the analysis struggled with reads first.
 */
export function dataQualityRead(result: Result, slices: Slices): DataQualityRead {
  const quality = result.data_quality;
  const signalMap = slices.signal_map ?? [];
  const tagForRole = new Map(signalMap.map((entry) => [entry.signal_role, entry.source_tag]));

  const roles = (quality.role_counts ?? [])
    .map((row) => ({
      signalRole: row.signal_role,
      sourceTag: tagForRole.get(row.signal_role) ?? row.signal_role,
      missing: row.missing,
      bad: row.bad,
      warmUp: row.warm_up,
      unitMismatch: row.unit_mismatch,
    }))
    .sort((a, b) => {
      const trouble = b.missing + b.bad - (a.missing + a.bad);
      if (trouble !== 0) return trouble;
      return a.signalRole.localeCompare(b.signalRole);
    });

  return {
    evaluability: quality.evaluability,
    reason: quality.reason,
    confidence: quality.confidence,
    confidencePct: quality.confidence === null ? null : Math.round(quality.confidence * 100),
    missingPoints: quality.missing_points,
    badQualityPoints: quality.bad_quality_points,
    roles,
  };
}

export type ExcursionRow = {
  id: string;
  ruleLabel: string;
  state: NonNullable<Result['excursions']>[number]['state'];
  direction: NonNullable<Result['excursions']>[number]['direction'];
  heldMinutes: number;
  heldLabel: string;
  heldAggregate: boolean;
  firstAt: string;
  lastAt: string;
};

/** `rl-zn-ph-limit` → `pH limit`; `rl-zn-ph-trend` → `pH trend`; else the id. */
export function ruleLabel(ruleId: string, unit = 'pH'): string {
  const label = unit === 'pH' ? 'pH' : unit === '°C' ? 'Temperature' : unit;
  if (ruleId.endsWith('-limit')) return `${label} limit`;
  if (ruleId.endsWith('-trend')) return `${label} trend`;
  return ruleId;
}

/** One row per excursion the envelope reported, in the order it reported them. */
export function excursionRows(result: Result): ExcursionRow[] {
  return (result.excursions ?? []).map((excursion) => {
    const heldMinutes = excursion.held_minutes ?? 0;
    const firstAtMs = Date.parse(excursion.first_event_time);
    const lastAtMs = Date.parse(excursion.last_event_time);
    const observedSpanMinutes = Number.isFinite(firstAtMs) && Number.isFinite(lastAtMs)
      ? Math.floor((lastAtMs - firstAtMs) / 60_000)
      : heldMinutes;
    return {
      id: excursion.excursion_id,
      ruleLabel: ruleLabel(excursion.rule_ids[0] ?? excursion.excursion_id),
      state: excursion.state,
      direction: excursion.direction ?? null,
      heldMinutes,
      heldLabel: formatElapsedMinutes(heldMinutes),
      heldAggregate: heldMinutes > 0 && observedSpanMinutes > heldMinutes,
      firstAt: envelopeClock(excursion.first_event_time),
      lastAt: envelopeClock(excursion.last_event_time),
    };
  });
}

export function evaluabilityLabel(evaluability: DataQualityRead['evaluability']): string {
  switch (evaluability) {
    case 'non_evaluable':
      return 'Not evaluable';
    case 'suppressed':
      return 'Suppressed';
    default:
      return 'Evaluable';
  }
}

const REASON_LABEL: Record<NonNullable<DataQualityRead['reason']>, string> = {
  insufficient_baseline: 'Baseline too short',
  insufficient_window_data: 'Too few points in window',
  insufficient_warm_up: 'Still warming up',
  mode_not_applicable: 'Mode not applicable',
  unit_mismatch: 'Unit mismatch',
};

export function reasonLabel(reason: DataQualityRead['reason']): string | null {
  return reason === null ? null : REASON_LABEL[reason];
}

export function evaluabilityTone(
  evaluability: DataQualityRead['evaluability'],
): 'success' | 'warning' | 'danger' {
  if (evaluability === 'evaluable') return 'success';
  if (evaluability === 'suppressed') return 'warning';
  return 'danger';
}

/* -------------------------------------------------------------------------
   Operating mode — the window's mode timeline, drawn as one band.
   ------------------------------------------------------------------------- */

/**
 * The modes the fixture's knowledge slice names, decoded. The contract keeps
 * `Mode` a plain string, so a mode outside this map is not a bug: it renders
 * its raw id with the underscores opened out, on a neutral fill.
 */
export const MODE_LABEL: Record<string, string> = {
  normal: 'Normal',
  reduced_rate: 'Reduced rate',
  startup: 'Startup',
  shutdown: 'Shutdown',
};

export const MODE_TOKEN: Record<string, string> = {
  normal: 'var(--state-online)',
  reduced_rate: 'var(--state-warning)',
  startup: 'var(--state-info)',
  shutdown: 'var(--state-blocked)',
};

/** A mode the map does not name reads as ink, not as a colour that means nothing. */
const MODE_FILL_FALLBACK = 'color-mix(in srgb, var(--ink-tertiary) 40%, transparent)';

/**
 * A warm-up segment keeps its mode colour and takes this stripe over it. The
 * stripe is `--surface-2`, so it reads pale against the fill in light and dark
 * against it in dark — a hatch in both themes.
 */
export const WARM_UP_HATCH =
  'repeating-linear-gradient(135deg, transparent 0 3px, color-mix(in srgb, var(--surface-2) 55%, transparent) 3px 6px)';

export type ModeSegment = {
  /** The envelope's mode id, verbatim. */
  mode: string;
  label: string;
  fill: string;
  /** The timeline entry's own stamps, carried through untouched. */
  start: string;
  end: string;
  startClock: string;
  endClock: string;
  warmUp: boolean;
  /** The segment's share of the window, unrounded. */
  widthPct: number;
};

export type ModeBand = {
  segments: ModeSegment[];
  /** The modes present in the window, in the order the timeline first shows them. */
  legend: { mode: string; label: string; fill: string }[];
  modeAtEnd: string;
  modeAtEndLabel: string;
  uncertain: boolean;
};

/**
 * The operating-mode timeline as one band. The service already segmented the
 * window; this reads its segments, keeps their width proportional to duration
 * and decodes each mode. The only arithmetic is the width — no mode total, no
 * percentage, no count is derived here or anywhere downstream.
 */
export function modeBand(result: Result): ModeBand {
  const timeline = result.operating_mode?.timeline ?? [];
  const window = windowRead(result);
  const windowMinutes = diffMinutes(asCaseTime(window.end), asCaseTime(window.start));

  const segments: ModeSegment[] = timeline.map((segment) => {
    const start = envelopeClock(segment.start);
    const end = envelopeClock(segment.end);
    const minutes = diffMinutes(asCaseTime(end), asCaseTime(start));
    return {
      mode: segment.mode,
      label: MODE_LABEL[segment.mode] ?? segment.mode.replace(/_/g, ' '),
      fill: MODE_TOKEN[segment.mode] ?? MODE_FILL_FALLBACK,
      start: segment.start,
      end: segment.end,
      startClock: clock(start),
      endClock: clock(end),
      warmUp: segment.warm_up ?? false,
      widthPct: (minutes / windowMinutes) * 100,
    };
  });

  const legend: ModeBand['legend'] = [];
  const seen = new Set<string>();
  for (const segment of segments) {
    if (seen.has(segment.mode)) continue;
    seen.add(segment.mode);
    legend.push({ mode: segment.mode, label: segment.label, fill: segment.fill });
  }

  const modeAtEnd = result.operating_mode?.mode_at_end ?? '';
  return {
    segments,
    legend,
    modeAtEnd,
    modeAtEndLabel: MODE_LABEL[modeAtEnd] ?? modeAtEnd.replace(/_/g, ' '),
    uncertain: result.operating_mode?.uncertain ?? false,
  };
}

/* -------------------------------------------------------------------------
   Signal — the evaluations that fired, read from the envelope.

   Every number here is a field read: the limits are the service's own, the
   points are its evidence, and nothing is recomputed. The one arithmetic on
   this surface is chart geometry — the y-domain each rule kind needs — plus
   the × 100 that turns the signal map's `%` fractions into percents.
   ------------------------------------------------------------------------- */

export const KIND_LABEL: Record<string, string> = {
  absolute_limit: 'Limit',
  six_point_trend: 'Six-point trend',
};

export const ROLE_LABEL: Record<string, string> = {
  rougher_feed_ph: 'Rougher feed pH',
  rougher_recovery_proxy: 'Rougher recovery (proxy)',
};

export const INPUT_LABEL: Record<string, string> = {
  feed_rate: 'Feed rate',
  k_rate: 'Rate constant',
  r_ref: 'Reference recovery',
  reference_feed_rate: 'Reference feed rate',
  sigma_recovery: 'σ recovery',
};

export const SIGMA_SOURCE_LABEL: Record<string, string> = {
  sample: 'sample',
  mr: 'moving range',
};

/** Two decimals is the display ruling for this pH series (recorded assumption). */
const f2 = (value: number): string => value.toFixed(2);

/** The decode fallback every map on this surface shares: a raw id reads opened out. */
function openedOut(value: string): string {
  return value.replace(/_/g, ' ');
}

/** The unit the knowledge slice gives a signal role — never invented. */
function unitForRole(slices: Slices, signalRole: string): string | null {
  return (slices.signal_map ?? []).find((row) => row.signal_role === signalRole)?.unit ?? null;
}

export type AnalysisSignal = NonNullable<Result['spc_signals']>[number];
type EnvelopeSignal = AnalysisSignal;

type SignalLimits = {
  centre: number;
  ucl: number;
  lcl: number;
  baselineN: number;
  sigmaSource: NonNullable<EnvelopeSignal['limits']>['sigma_source'] | null;
};

/** The limits the service evaluated a signal against, when it carried all three. */
function signalLimits(signal: EnvelopeSignal): SignalLimits | null {
  const limits = signal.limits;
  if (!limits) return null;
  const { centre, ucl, lcl } = limits;
  if (centre === null || centre === undefined) return null;
  if (ucl === null || ucl === undefined) return null;
  if (lcl === null || lcl === undefined) return null;
  return {
    centre,
    ucl,
    lcl,
    baselineN: limits.baseline_n,
    sigmaSource: limits.sigma_source ?? null,
  };
}

export type SignalPoint = {
  /** The envelope's stamp as plant clock. */
  at: string;
  clock: string;
  value: number;
  /** Outside the service's own control limits — the fired fact, drawn in danger fill. */
  outside: boolean;
};

export type SignalChart = {
  points: SignalPoint[];
  centre: number;
  ucl: number;
  lcl: number;
  yMin: number;
  yMax: number;
  unit: string | null;
  caption: string;
};

/** The 12 % pad the control-chart kit uses (`ControlChart.tsx:203–206`). */
const DOMAIN_PAD = 0.12;

/**
 * The y-domain each rule kind needs, because the fired fact differs.
 *
 * A limit breach fired *because* the points left the limits, so the limits are
 * in the domain and the reader can see the crossing. A trend fired on the run
 * itself — on the domain shared with its limits, six points a few hundredths
 * apart would span two pixels — so the trend is drawn on its own points and a
 * limit is drawn only when it happens to fall inside.
 */
function domainFor(kind: string, values: number[], limits: SignalLimits): [number, number] {
  const refs = kind === 'six_point_trend' ? values : [limits.lcl, limits.ucl, ...values];
  const min = Math.min(...refs);
  const max = Math.max(...refs);
  const span = max - min || 1;
  return [min - span * DOMAIN_PAD, max + span * DOMAIN_PAD];
}

function chartCaption(signal: EnvelopeSignal, limits: SignalLimits, modeLabelText: string): string {
  const sigma = limits.sigmaSource;
  const sigmaLabel = SIGMA_SOURCE_LABEL[sigma ?? ''] ?? sigma ?? '';
  return [
    ruleLabel(signal.rule_id),
    modeLabelText,
    `${clock(envelopeClock(signal.segment_start))} → ${clock(envelopeClock(signal.segment_end))}`,
    `centre ${f2(limits.centre)}`,
    `UCL ${f2(limits.ucl)}`,
    `LCL ${f2(limits.lcl)}`,
    `baseline ${limits.baselineN}`,
    `σ ${sigmaLabel}`,
  ].join(' · ');
}

function buildChart(signal: EnvelopeSignal, slices: Slices, modeLabelText: string): SignalChart | null {
  const limits = signalLimits(signal);
  if (!limits) return null;
  if (signal.evidence_points.length === 0) return null;

  const points: SignalPoint[] = signal.evidence_points.map((point) => {
    const at = envelopeClock(point.event_time);
    return {
      at,
      clock: clock(at),
      value: point.value,
      outside: point.value < limits.lcl || point.value > limits.ucl,
    };
  });

  const [yMin, yMax] = domainFor(signal.kind, points.map((point) => point.value), limits);

  return {
    points,
    centre: limits.centre,
    ucl: limits.ucl,
    lcl: limits.lcl,
    yMin,
    yMax,
    unit: unitForRole(slices, signal.signal_role),
    caption: chartCaption(signal, limits, modeLabelText),
  };
}

export type FiredSignalRow = {
  ruleId: string;
  ruleLabel: string;
  kind: string;
  kindLabel: string;
  mode: string;
  modeLabel: string;
  segmentStart: string;
  segmentEnd: string;
  segmentStartClock: string;
  segmentEndClock: string;
  heldMinutes: number;
  heldLabel: string;
  direction: 'rising' | 'falling' | null;
  pointCount: number;
  pointsLabel: string;
  /** `ruleId · segment start → segment end`, both carrying their date. */
  title: string;
  charted: boolean;
  chart: SignalChart | null;
};

/**
 * Every evaluation the service fired, in the order it reported them. The first
 * fired entry of each rule carries the chart; later entries of the same rule
 * are list-only, because the service re-baselines per mode run and their limits
 * differ — joining them into one line would draw a series that never existed.
 */
export function firedSignals(result: Result, slices: Slices): FiredSignalRow[] {
  const fired = (result.spc_signals ?? []).filter((signal) => signal.fired === true);
  const considered = new Set<string>();

  return fired.map((signal) => {
    const firstForRule = !considered.has(signal.rule_id);
    if (firstForRule) considered.add(signal.rule_id);
    const chart = firstForRule ? buildChart(signal, slices, modeLabel(signal.mode)) : null;
    const heldMinutes = signal.held_minutes ?? 0;
    const pointCount = signal.evidence_points.length;
    const segmentStart = envelopeClock(signal.segment_start);
    const segmentEnd = envelopeClock(signal.segment_end);

    return {
      ruleId: signal.rule_id,
      ruleLabel: ruleLabel(signal.rule_id, unitForRole(slices, signal.signal_role) ?? ''),
      kind: signal.kind,
      kindLabel: KIND_LABEL[signal.kind] ?? openedOut(signal.kind),
      mode: signal.mode,
      modeLabel: modeLabel(signal.mode),
      segmentStart,
      segmentEnd,
      segmentStartClock: clock(segmentStart),
      segmentEndClock: clock(segmentEnd),
      heldMinutes,
      heldLabel: formatElapsedMinutes(heldMinutes),
      direction: signal.direction ?? null,
      pointCount,
      pointsLabel: `${pointCount} point${pointCount === 1 ? '' : 's'}`,
      title: `${signal.rule_id} · ${segmentStart} → ${segmentEnd}`,
      charted: chart !== null,
      chart,
    };
  });
}

function modeLabel(mode: string): string {
  return MODE_LABEL[mode] ?? openedOut(mode);
}

/** Chart one envelope signal without rebuilding the caller's result projection. */
export function analysisSignalChart(signal: AnalysisSignal, slices: Slices): SignalChart | null {
  return buildChart(signal, slices, modeLabel(signal.mode));
}

export type SignalSummary = {
  total: number;
  fired: number;
  notFired: number;
  /** Evaluated, clean — the rules that looked and found nothing. */
  evaluatedClean: number;
  notEvaluated: number;
  reasons: { reason: string; label: string; count: number }[];
  suppressedModes: { mode: string; label: string; count: number }[];
};

/**
 * The non-fired evaluations, counted.
 *
 * These counts are the one place this surface counts, and they count envelope
 * rows — a count of the list the service returned is a read of that list, not a
 * derived plant figure. The reason and suppression buckets are taken over the
 * not-evaluated entries only (a fired or clean entry carries neither), and each
 * list sorts by count descending, then by label ascending.
 */
export function signalSummary(result: Result): SignalSummary {
  const signals = result.spc_signals ?? [];
  const fired = signals.filter((signal) => signal.fired === true).length;
  const notEvaluatedEntries = signals.filter((signal) => signal.not_evaluated === true);

  const reasonCounts = new Map<NonNullable<EnvelopeSignal['reason']>, number>();
  const modeCounts = new Map<string, number>();
  for (const signal of notEvaluatedEntries) {
    if (signal.reason !== null) {
      reasonCounts.set(signal.reason, (reasonCounts.get(signal.reason) ?? 0) + 1);
    }
    const mode = signal.suppressed_by_mode;
    if (mode) modeCounts.set(mode, (modeCounts.get(mode) ?? 0) + 1);
  }

  const reasons = [...reasonCounts.entries()]
    .map(([reason, count]) => ({ reason: reason as string, label: reasonLabel(reason) ?? reason, count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const suppressedModes = [...modeCounts.entries()]
    .map(([mode, count]) => ({ mode, label: modeLabel(mode), count }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label));

  const notEvaluated = notEvaluatedEntries.length;
  const notFired = signals.length - fired;

  return {
    total: signals.length,
    fired,
    notFired,
    evaluatedClean: notFired - notEvaluated,
    notEvaluated,
    reasons,
    suppressedModes,
  };
}

export type ExpectationRow = {
  signalRole: string;
  label: string;
  unit: string | null;
  observed: number;
  expected: number;
  bandLow: number;
  bandHigh: number;
  shortfall: number;
  observedLabel: string;
  expectedLabel: string;
  bandLabel: string;
  shortfallLabel: string;
  tone: VizTone;
  inputs: { key: string; label: string; value: string }[];
  /** `signalRole · <input label> <value> · …` — the traceability record. */
  title: string;
};

/** Four significant figures for a raw input, without a trailing zero tail. */
function inputValue(value: number): string {
  return String(Number(value.toPrecision(4)));
}

/** `%` in the slice's own unit means the envelope's fraction × 100. */
function pct(value: number): string {
  return `${(value * 100).toFixed(2)} %`;
}

/**
 * One row per expectation the service evaluated. The signal map says the role's
 * unit is `%` and the envelope carries fractions, so every label is that
 * fraction × 100; the fields themselves pass through unrounded.
 */
export function expectationRows(result: Result, slices: Slices): ExpectationRow[] {
  return (result.expectations ?? []).map((expectation) => {
    const signalRole = expectation.signal_role;
    const { observed, expected, band_low: bandLow, band_high: bandHigh, shortfall } = expectation;

    const inputs = Object.entries(expectation.inputs_used).map(([key, value]) => ({
      key,
      label: INPUT_LABEL[key] ?? openedOut(key),
      value: inputValue(value),
    }));

    const bandLabel = `${(bandLow * 100).toFixed(2)} – ${(bandHigh * 100).toFixed(2)} %`;

    return {

      signalRole,
      label: ROLE_LABEL[signalRole] ?? openedOut(signalRole),
      unit: unitForRole(slices, signalRole),
      observed,
      expected,
      bandLow,
      bandHigh,
      shortfall,
      observedLabel: pct(observed),
      expectedLabel: `Expected ${pct(expected)}`,
      bandLabel,
      shortfallLabel: `${(shortfall * 100).toFixed(2)} pt short`,
      tone: observed < bandLow ? 'bad' : observed < expected ? 'warn' : 'ok',
      inputs,
      title: `${signalRole} · band ${bandLabel} · ${inputs
        .map((input) => `${input.label} ${input.value}`)
        .join(' · ')}`,
    };
  });
}

/* -------------------------------------------------------------------------
   Estimated EBITDA impact — the envelope's own money read.

   Two headline figures and the three lines that make them up. Every number
   is a field read through `formatCurrency`; the only decoding is the basis label,
   the title's raised first letter and the per-component sign, each of which
   reads a field the envelope or its knowledge slice already carries.
   ------------------------------------------------------------------------- */

/**
 * D17: the one short name for the case's run estimate. The header, the drawer's
 * value section and the agent band all read this constant, so a viewer never
 * sees two per-day figures for one case with only one of them named.
 */
export const RUN_ESTIMATE_LABEL = 'Run estimate · if the rougher gap closes';

/**
 * The bases the contract closes. `Basis` is a union of three, so this map is
 * total over it; the `_`-opened fallback is for a service that widens the
 * union ahead of this build, exactly as the mode map's fallback is.
 */
export const BASIS_LABEL: Record<string, string> = {
  budget: 'Budget',
  latest_estimate: 'Latest estimate',
  stretch: 'Stretch',
};

export type FinancialComponentRow = {
  /** The value-driver node id, verbatim — never rendered; the label is. */
  node: string;
  label: string;
  value: number;
  valueLabel: string;
  /** The term's own sign in the impact formula: the leading term reads `''`. */
  sign: '' | '−';
  unit: string;
  formula: string;
};

export type FinancialRead = {
  /** The envelope's `label` with its first letter raised — never retyped. */
  title: string;
  basis: string;
  basisLabel: string;
  currency: string;
  formulaRev: string;
  perHour: number;
  perDay: number;
  perHourLabel: string;
  perDayLabel: string;
  tone: VizTone;
  components: FinancialComponentRow[];
};

/**
 * The component's sign as the impact formula states it.
 *
 * `gross_metal_value - realisation_deduction - variable_cost_change` is the
 * impact node's own formula in the value DAG. A term the formula subtracts
 * reads with a minus; the leading term and any added term read unsigned. This
 * is a string decode of a field, the same class of operation as `BASIS_LABEL`
 * — nothing is summed, and a slice that carries no impact formula yields no
 * sign at all rather than an assumed one.
 */
function componentSign(formula: string, node: string, value: number): '' | '−' {
  if (!formula) return '';
  // A zero carries no sign: a signed zero would say the component pulled the
  // impact down when it moved nothing.
  if (formatCurrency(value) === formatCurrency(0)) return '';
  return new RegExp(`-\\s+${node}\\b`).test(formula) ? '−' : '';
}

/**
 * The estimated EBITDA impact, or `null` when the envelope carries none.
 *
 * `null` is the absence branch: the service reports the impact only when it
 * was requested and evaluable, and a section that renders a caveat for its own
 * absence would be telling the reader about the plumbing rather than the
 * plant. A positive figure is a loss on this envelope — its label says impact
 * and its components are value lost — so it reads in the danger tone; a
 * negative figure reads as a gain, and a zero is a measurement, read neutral.
 */
export function financialRead(result: Result, slices: Slices): FinancialRead | null {
  const impact = result.financial_impact;
  if (!impact) return null;

  // One basis (D14 / K7): the shared formatter prints the dollar glyph whatever
  // the envelope says, so a non-USD deck fails here rather than on the glass.
  assertUsd(impact.currency);

  const dagNodes = slices.value_dag?.nodes ?? [];
  const labelFor = (node: string): string =>
    dagNodes.find((entry) => entry.node_id === node)?.label ?? openedOut(node);

  const impactFormula =
    dagNodes.find((entry) => entry.node_id === slices.financial_basis?.impact_per_hour_node)
      ?.formula ?? '';

  const perHour = impact.impact_per_hour;

  return {
    title: impact.label.charAt(0).toUpperCase() + impact.label.slice(1),
    basis: impact.basis,
    basisLabel: BASIS_LABEL[impact.basis] ?? openedOut(impact.basis),
    currency: impact.currency,
    formulaRev: impact.formula_rev,
    perHour,
    perDay: impact.impact_per_day,
    perHourLabel: formatCurrency(perHour),
    perDayLabel: formatCurrency(impact.impact_per_day),
    tone: perHour > 0 ? 'bad' : perHour < 0 ? 'ok' : 'neutral',
    // The envelope's own order: the breakdown reads the way the service
    // states it, from gross value down to the cost change.
    components: impact.components.map((component) => ({
      node: component.node,
      label: labelFor(component.node),
      value: component.value,
      // The component table's column header carries the one glyph; each value
      // reads bare so the drawer never prints the glyph twice on one row.
      valueLabel: formatCurrencyBare(component.value),
      sign: componentSign(impactFormula, component.node, component.value),
      unit: component.unit,
      formula: component.formula,
    })),
  };
}

/* -------------------------------------------------------------------------
   Process effect — how far the loss travels, and the value path that turns
   it into dollars.

   Both reads are field reads. The envelope carries node ids; every human
   label comes from the knowledge slice the run was evaluated against, so a
   node the slice does not name opens its id out rather than rendering a slug.
   ------------------------------------------------------------------------- */

export type PropagationRow = {
  depth: number;
  fromLabel: string;
  toLabel: string;
  viaLabels: string[];
  effect: string;
  magnitude: number | null;
  /** The fraction as percentage points, or empty when the path carries none. */
  magnitudeLabel: string;
  /** `effect · from → to [via …] · magnitude unit` — ids live here, not on the glass. */
  title: string;
};

export type PropagationRead = {
  rows: PropagationRow[];
  count: number;
  maxDepth: number;
  /** The deepest path, first in envelope order — the chain the section leads with. */
  headline: { labels: string[]; magnitudeLabel: string };
};

/** The process graph's own words for a node id. */
function processLabel(slices: Slices, nodeId: string): string {
  return (
    (slices.process_graph?.nodes ?? []).find((node) => node.node_id === nodeId)?.label ??
    openedOut(nodeId)
  );
}

/**
 * Every path the service inspected, shallowest first.
 *
 * The order is the envelope's within each depth: the sort is stable, so the
 * eight rows read as four rungs of a chain rather than as a re-ranked list.
 * `maxDepth` is what the reader is not told — the depth bound is the service's
 * own configuration and is asserted, never rendered.
 */
export function propagationRows(result: Result, slices: Slices): PropagationRead | null {
  const paths = result.process_propagation?.paths;
  if (!paths) return null;

  const rows: PropagationRow[] = paths
    .map((path) => {
      const magnitude = path.magnitude ?? null;
      const via = path.via;
      return {
        depth: path.depth,
        fromLabel: processLabel(slices, path.from_node),
        toLabel: processLabel(slices, path.to_node),
        viaLabels: via.map((nodeId) => processLabel(slices, nodeId)),
        effect: path.effect,
        magnitude,
        magnitudeLabel: magnitude === null ? '' : pt(magnitude),
        title: `${path.effect} · ${path.from_node} → ${path.to_node}${
          via.length ? ` via ${via.join(', ')}` : ''
        } · ${magnitude ?? ''} ${path.unit}`,
      };
    })
    .sort((a, b) => a.depth - b.depth);

  const maxDepth = rows.reduce((deepest, row) => (row.depth > deepest ? row.depth : deepest), 0);
  const deepestRow = rows.find((row) => row.depth === maxDepth);

  return {
    rows,
    count: rows.length,
    maxDepth,
    headline: {
      labels: deepestRow
        ? [deepestRow.fromLabel, ...deepestRow.viaLabels, deepestRow.toLabel]
        : [],
      magnitudeLabel: deepestRow?.magnitudeLabel ?? '',
    },
  };
}

export type ValuePathRow = {
  nodeId: string;
  label: string;
  value: number;
  unit: string | null;
  valueLabel: string;
  formula: string | null;
  inputs: Record<string, number>;
  /** `formula · <input> <value> · …` — the substitution record, and only there. */
  title: string;
};

/** The value DAG's own words for a node id. */
function valueLabel(nodeId: string, slices: Slices): string {
  return (
    (slices.value_dag?.nodes ?? []).find((node) => node.node_id === nodeId)?.label ??
    openedOut(nodeId)
  );
}

/** The row's figure, decoded by the unit the value DAG gives the node. */
function valueFigure(value: number, unit: string | null): string {
  if (unit === 'USD/h') return formatCurrency(value);
  if (unit === 't/h') return `${value.toFixed(3)} t/h`;
  if (unit === 'dmt/h') return `${value.toFixed(3)} dmt/h`;
  if (unit === 'fraction') return pt(value);
  if (unit === null) return String(value);
  return `${value} ${unit}`;
}

/**
 * The chain from the first quantity the excursion moved to the impact node the
 * card above already shows, in the order the service computed it.
 *
 * The envelope's `value_path` array is dependency-ordered, not compute-ordered:
 * it lists freight before the gross metal value it is deducted from. The rows
 * therefore follow the value DAG's declared node order — the formula order the
 * service evaluated — so the table reads gross, then the deductions, then the
 * impact. A node the DAG does not name keeps its envelope position, after the
 * named ones. `value`, `unit`, `formula` and `inputs` are referential — only
 * the label string is decoded, and it comes from the value DAG.
 */
export function valuePathRows(result: Result, slices: Slices): ValuePathRow[] | null {
  const path = result.value_path;
  if (!path) return null;

  const declaredOrder = new Map(
    (slices.value_dag?.nodes ?? []).map((node, index) => [node.node_id, index]),
  );
  const ordered = path
    .map((node, index) => ({ node, index }))
    .sort((left, right) => {
      const leftRank = declaredOrder.get(left.node.node_id) ?? Number.MAX_SAFE_INTEGER;
      const rightRank = declaredOrder.get(right.node.node_id) ?? Number.MAX_SAFE_INTEGER;
      return leftRank - rightRank || left.index - right.index;
    })
    .map(({ node }) => node);

  return ordered.map((node) => {
    const formula = node.formula ?? null;
    const unit = node.unit ?? null;
    const entries = Object.entries(node.inputs);
    return {
      nodeId: node.node_id,
      label: valueLabel(node.node_id, slices),
      value: node.value,
      unit,
      valueLabel: valueFigure(node.value, unit),
      formula,
      inputs: node.inputs,
      title: `${formula ?? '—'}${
        entries.length ? ` · ${entries.map(([key, value]) => `${key} ${value}`).join(' · ')}` : ''
      }`,
    };
  });
}

/** A fraction on this surface reads as percentage points, × 100 once. */
export function pt(fraction: number): string {
  return `${(fraction * 100).toFixed(2)} pt`;
}

/* -------------------------------------------------------------------------
   The knowledge slice — the FMEA cause, the Control Plan row the signal
   should have held and the TARP ladder that answers it.

   The envelope carries ids; every human label lives in the knowledge slice
   the run was evaluated against. A cause the slice does not label is absence,
   never a slug on the glass — the id resolves, and the row is still dropped.
   ------------------------------------------------------------------------- */

/** A plant role id, short-labelled where the org seed names it. */
export function plantRoleLabel(role: string): string {
  if (role === 'cro') return 'Control room';
  const known = ROLE_SHORT_LABEL[role as PlantRoleId];
  if (known) return known;
  const opened = role.replace(/[-_]/g, ' ');
  return opened.charAt(0).toUpperCase() + opened.slice(1);
}

export type CauseRow = {
  causeId: string;
  label: string;
  effectLabel: string | null;
  /** The slice's own rule list, referentially. */
  ruleIds: string[];
  /** `cause_id · effect_id · rules <ids>` — ids live here, not on the glass. */
  title: string;
};

export type ControlPlanRead = {
  rowId: string;
  /** The signal map's own tag for the role, or the role opened out. */
  signalLabel: string;
  /** `9.5–11 pH` — the slice's limits and its own unit, en dash, no spaces. */
  specLabel: string;
  unit: string;
  /** `95 %` — the one multiplication on this read. */
  reliabilityLabel: string;
  /** `row_id · signal_role` — the traceability record. */
  title: string;
};

export type TierRow = {
  tier: number;
  ownerLabel: string;
  instruction: string;
  minutesLabel: string;
  escalateLabel: string;
  /** `<tier> · <owner> · <instruction> · <minutes> → <escalate to>`. */
  line: string;
};

export type LadderRead = {
  ladderId: string;
  /** Ascending by `tier`; the slice's own order for ties. */
  tiers: TierRow[];
  /** `ladder_id · rules <ids>` — the traceability record. */
  title: string;
};

export type KnowledgeRead = {
  causes: CauseRow[];
  controlPlan: ControlPlanRead[];
  ladders: LadderRead[];
};

/** Sentence-form the TARP source instruction for a reader without changing its seed value. */
export function sentenceTarpInstruction(instruction: string): string {
  const trimmed = instruction.trim();
  if (!trimmed) return trimmed;
  const sentence = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
}

/** Preserve the slice order when there is no ranked diagnosis; otherwise follow its cause ranking. */
export function diagnosisOrderedCauses(
  causes: CauseRow[],
  diagnosis: Result['diagnosis'],
): CauseRow[] {
  if (!diagnosis?.candidates.length) return causes;
  const rank = new Map(diagnosis.candidates.map((candidate, index) => [candidate.cause_id, index]));
  return [...causes].sort(
    (left, right) =>
      (rank.get(left.causeId) ?? Number.MAX_SAFE_INTEGER) -
      (rank.get(right.causeId) ?? Number.MAX_SAFE_INTEGER),
  );
}

/**
 * The FMEA causes, Control Plan rows and TARP ladders the envelope's `links`
 * cite, resolved against the knowledge slice.
 *
 * Every list is one entry per linked id that the slice carries, in the order
 * `links` names them. A cause the slice carries no `cause_label` for is
 * dropped rather than rendered as its id: an unlabelled cause is absence, and
 * a slug is not a label. `null` when the envelope carries no `links` at all.
 */
export function knowledgeRead(result: Result, slices: Slices): KnowledgeRead | null {
  const links = result.links;
  if (!links) return null;

  const signalMap = slices.signal_map ?? [];
  const tagForRole = new Map(signalMap.map((entry) => [entry.signal_role, entry.source_tag]));

  const causes: CauseRow[] = [];
  for (const causeId of links.fmea_cause_ids) {
    const link = slices.fmea.find((entry) => entry.cause_id === causeId);
    if (!link) continue;
    const label = link.cause_label;
    if (label === null || label === undefined || label === '') continue;
    causes.push({
      causeId,
      label,
      effectLabel: link.effect_label ?? null,
      ruleIds: link.rule_ids,
      title: [causeId, link.effect_id, `rules ${link.rule_ids.join(', ')}`].join(' · '),
    });
  }

  const controlPlan: ControlPlanRead[] = [];
  for (const rowId of links.control_plan_row_ids) {
    const row = slices.control_plan.find((entry) => entry.row_id === rowId);
    if (!row) continue;
    controlPlan.push({
      rowId,
      signalLabel: tagForRole.get(row.signal_role) ?? openedOut(row.signal_role),
      specLabel: row.spec_low == null
        ? `${String(row.spec_high)} ${row.unit} high`
        : `${String(row.spec_low)}–${String(row.spec_high)} ${row.unit}`,
      unit: row.unit,
      reliabilityLabel: `${Math.round(row.reliability * 100)} %`,
      title: `${rowId} · ${row.signal_role}`,
    });
  }

  const ladders: LadderRead[] = [];
  for (const ladderId of links.tarp_ladder_ids) {
    const ladder = slices.tarp.find((entry) => entry.ladder_id === ladderId);
    if (!ladder) continue;
    const tiers = [...ladder.tiers]
      .sort((a, b) => a.tier - b.tier)
      .map((tier) => {
        const ownerLabel = plantRoleLabel(tier.owner_role);
        const escalateLabel = plantRoleLabel(tier.escalate_to_role);
        const minutesLabel = formatElapsedMinutes(tier.escalate_after_minutes);
        return {
          tier: tier.tier,
          ownerLabel,
          instruction: tier.instruction,
          minutesLabel,
          escalateLabel,
          line: `${tier.tier} · ${ownerLabel} · ${tier.instruction} · ${minutesLabel} → ${escalateLabel}`,
        };
      });
    ladders.push({
      ladderId,
      tiers,
      title: `${ladderId} · rules ${ladder.rule_ids.join(', ')}`,
    });
  }

  return { causes, controlPlan, ladders };
}

export type ProvenanceRead = {
  /** `10 knowledge revisions` — the count and nothing else. */
  text: string;
  /** The ten `<kind> <rev>` pairs, the service version and the computed instant. */
  title: string;
  count: number;
};

/**
 * The revisions the run was computed against, counted for the glass and
 * spelled out for the tooltip.
 *
 * The count is `Object.keys(revisions).length` — the envelope's own ten kinds,
 * not a literal. The tooltip is the traceability record and keeps the
 * contract's own spelling (`plant_model_rev pm-zn-v3`).
 */
export function provenanceText(result: Result): ProvenanceRead {
  const revisions = result.revisions;
  const kinds = Object.keys(revisions).sort();
  const count = kinds.length;
  const pairs = kinds.map((kind) => `${kind} ${revisions[kind as keyof typeof revisions]}`);
  return {
    text: `${count} knowledge revisions`,
    title: `${pairs.join(' · ')} · service ${result.service_version} · computed ${result.computed_at}`,
    count,
  };
}
